import dynamic from "next/dynamic";
import Link from "next/link";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";
import Head from "next/head";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

technologyConsulting.title = "Technology Consulting — Technical Due Diligence & Stack Migrations | ITMTB";
technologyConsulting.desc =
  "ITMTB provides technical due diligence, stack migrations, and performance consulting for enterprise technology decisions — the team you bring in before replacing your stack.";
technologyConsulting.header_img = "https://www.itmtb.com/services/capability/tech consulting.webp";
technologyConsulting.keywords =
  "technology consulting India enterprise, technical due diligence India, stack migration consulting India, CTO advisory India, pre-investment tech audit India, technology modernisation India";

const faqItems = [
  {
    q: "What is technical due diligence and when do I need it?",
    a: "Technical due diligence is an independent assessment of a software system or technology stack, produced for a decision-maker who needs to understand what they're inheriting or investing in. The most common trigger points: pre-acquisition (what is the technical liability of the company we're buying?); pre-investment (is this startup's technology actually built, or is it held together with duct tape?); pre-rebuild (does this system actually need to be replaced, or can it be extended?); and pre-AI deployment (is this stack ready to integrate with AI agents, or does it need modernisation first?). The output is a written assessment covering architecture, security posture, scalability limits, technical debt inventory, dependency risks, and a prioritised list of findings with estimated remediation effort. ITMTB has conducted technical due diligence for fintech platforms processing millions of transactions, startup technology stacks ahead of funding rounds, and legacy enterprise systems being evaluated for AI-readiness.",
  },
  {
    q: "What does a technology stack migration actually involve?",
    a: "A stack migration is a change to the underlying technology that runs a system — examples: Python 2 to Python 3, .NET Framework to .NET 8, Java EE to Spring Boot, monolith to microservices, on-premises to cloud, one cloud provider to another. The process has consistent steps regardless of the specific migration: (1) inventory — map all dependencies, interfaces, and integration points of the current system; (2) risk assessment — identify which components are most likely to behave differently after migration and what the failure modes are; (3) migration runbook — define the exact sequence of changes, the testing approach at each step, and the rollback procedure if a step fails; (4) execution — migrate progressively rather than as a big-bang cutover; (5) validation — test the migrated system against the original behaviour baseline. The biggest migration risk is undocumented behaviour: things the system does that nobody wrote down. We find those during the inventory phase before migration starts.",
  },
  {
    q: "What is performance consulting and when is it worth doing?",
    a: "Performance consulting is an investigation into why a software system is slower, more expensive to run, or less reliable than it should be — and a set of specific, prioritised recommendations for fixing it. It's worth doing when: application response times are degrading as usage grows and the cause isn't obvious; cloud costs are increasing faster than load; a system that worked fine at small scale is becoming unreliable at production load; or you're about to add AI inference to a system and need to understand whether the current infrastructure can handle the additional load. Our performance consulting engagements start with instrumentation — adding the observability needed to diagnose the problem rather than guessing. Common findings: N+1 query problems that only appear under concurrent load, cache invalidation logic that's too aggressive, database indices that cover development data volumes but not production, and synchronous API calls that should be async.",
  },
  {
    q: "How do you approach the decision to rebuild vs. extend a legacy system?",
    a: "Most of the time, extending is the right answer — and most organisations that rebuild from scratch find themselves 18 months later with a new system that has different problems but is no faster or cheaper. Rebuilding is genuinely warranted when: the existing system uses technology that is no longer maintained (Python 2, .NET Framework with no upgrade path, a vendor whose product is sunset); the architecture makes the system fundamentally unable to meet new requirements (synchronous-only when you need real-time, single-region when you need multi-region); or the cost of adding any new feature is so high due to technical debt that incremental extension is more expensive than rewrite over a 3-year horizon. In all other cases, a strangler-fig extension approach — wrapping the legacy system with a new interface and migrating functionality progressively — delivers the modernisation benefits without the timeline and risk of a full rebuild. We assess which path is appropriate during the discovery sprint, with a 3-year TCO comparison of both options.",
  },
  {
    q: "Do you provide CTO-level technology advisory on an ongoing basis?",
    a: "Yes — for organisations without a full-time CTO or where the CTO needs a specialist partner for a specific domain (AI deployment, cloud architecture, security), we provide fractional technology advisory. This covers: attending architecture review sessions and providing independent assessment of proposals; reviewing vendor RFPs and evaluating responses; advising on build vs. buy decisions with a documented recommendation; advising on hiring — technical interview panels and role definition for engineering hires; and providing a second opinion before major technology investment decisions. The engagement is structured as a fixed monthly retainer with a defined scope of advisory hours and access to ITMTB's delivery team for specific investigations. Most fractional CTO engagements run 3–6 months during a period of significant technology decision-making.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
technologyConsulting.faq = faqItems;

function technologyConsulting() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let filteredInsights = insights.filter(item => item?.meta?.refer?.includes("Technology Consulting"));
  let filteredCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("Technology Consulting"));

  if (filteredInsights.length === 1) {
    insights = [...filteredInsights, ...getRandom(insights.filter(item => !filteredInsights.includes(item)), 1)];
  } else if (filteredInsights.length >= 2) {
    insights = getRandom(filteredInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (filteredCaseStudies.length === 1) {
    caseStudies = [...filteredCaseStudies, ...getRandom(caseStudies.filter(item => !filteredCaseStudies.includes(item)), 1)];
  } else if (filteredCaseStudies.length >= 2) {
    caseStudies = getRandom(filteredCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  const subServices = [
    {
      img: null,
      key: "Technical Due Diligence",
      title: "The independent assessment you commission before making a major technology decision.",
      text: "Pre-acquisition, pre-investment, pre-rebuild, or pre-AI deployment: we produce a written technical assessment covering architecture, security posture, scalability limits, technical debt inventory, and a prioritised findings list with estimated remediation effort.\n\nUsed by investors evaluating startup technology before funding rounds, enterprise buyers assessing the technical liability of an acquisition, and product leaders deciding whether to rebuild or extend a legacy system.",
    },
    {
      img: null,
      key: "Technical Migrations",
      title: "Stack migrations executed from a tested runbook — with a defined rollback path at every step.",
      text: "Migration paths we've executed: Python 2 to 3, .NET Framework to .NET 8, Java EE to Spring Boot, monolith to microservices, on-premises to cloud, legacy hosting to AWS/Azure. Every migration starts with a dependency inventory and risk assessment to find the undocumented behaviour that causes migration failures — before the migration starts.\n\nWe produce the migration runbook as a deliverable, so your team can execute, validate, and roll back with full visibility at each step.",
    },
    {
      img: null,
      key: "Performance Consulting",
      title: "Diagnose why your system is slower or more expensive than it should be — then fix the specific problems.",
      text: "Performance consulting starts with instrumentation: adding the observability needed to diagnose the actual bottleneck rather than guessing. Common findings include N+1 query problems that only appear under concurrent load, caches that are invalidated too aggressively, database indices that cover development data volumes but not production, and synchronous calls that should be async.\n\nFor cloud-hosted systems, we also review cost efficiency — slow queries that run frequently are expensive, and the performance and cost fixes are often the same change.",
    },
    {
      img: null,
      key: "Fractional CTO and Technology Advisory",
      title: "Specialist technology leadership for organisations making major technology decisions without a full-time CTO.",
      text: "We provide fractional CTO advisory for organisations at a technology inflection point: pre-AI deployment, during a major cloud migration, ahead of a significant engineering hire, or when evaluating a major vendor or platform decision.\n\nThe engagement covers architecture review sessions, vendor RFP evaluation, build vs. buy analysis, and technical interview panels for senior engineering hires — structured as a monthly retainer with defined advisory scope. Most engagements run 3–6 months.",
    },
  ];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              name: "Technology Consulting — Technical Due Diligence & Stack Migrations India",
              url: "https://www.itmtb.com/services/technology-consulting",
              image: "https://www.itmtb.com/services/capability/tech consulting.webp",
              description:
                "ITMTB provides technical due diligence, stack migrations, and performance consulting for enterprise technology decisions — the team you bring in before replacing your stack.",
              provider: {
                "@type": "Organization",
                name: "ITMTB Technologies",
                url: "https://www.itmtb.com",
                logo: "https://www.itmtb.com/logo.webp",
              },
              areaServed: [{ "@type": "Place", name: "India" }],
              serviceType: [
                "Technology Consulting India",
                "Technical Due Diligence India",
                "Stack Migration Consulting",
                "CTO Advisory India",
                "Pre-Investment Tech Audit India",
                "Technology Modernisation India",
                "Performance Consulting",
                "Engineering Due Diligence",
              ],
              sameAs: [
                "https://www.linkedin.com/company/itmtb-technologies/",
                "https://x.com/ITMTBTech",
                "https://www.instagram.com/itmtbtech/",
              ],
            }),
          }}
        />
      </Head>

      <Service2
        heading="Technology Consulting"
        paragraph={`Technical due diligence and stack migrations — the team you bring in before you replace your stack.

Most rebuild decisions are made without a rigorous assessment of the alternative: extend. Most stack migrations fail not in execution but in the inventory phase — undocumented behaviour that nobody mapped before migration started. Most technology decisions made under deadline pressure get revisited at significant cost 18 months later.

We've conducted technical due diligence for fintechs processing millions of transactions daily, startup technology stacks ahead of funding rounds, and legacy enterprise systems being evaluated for AI-readiness. We give you the independent view before the commitment.`}
        bgPath="/services/capability/tech consulting.webp"
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag="Technology Consulting"
        maxItems={3}
        heading="Selected work in technology consulting"
      />

      <CustomerSection title="Trusted by" />

      <MethodologyAnchor />

      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      {/* § Related capabilities */}
      <section className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <p className="text-gray-700 text-base md:text-lg leading-relaxed max-w-3xl mb-10">
            A rebuild-versus-extend decision turns on numbers that are easy to get wrong. Before committing, it helps to understand{" "}
            <Link href="/blog/custom-software-development-cost-india">
              <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">what custom software actually costs to own in India</a>
            </Link>{" "}
            — pricing models, support SLAs, and the lifecycle spend that follows delivery.
          </p>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-7">
            Related capabilities
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                href: "/services/forward-deployed-engineering",
                label: "Forward Deployed Engineering",
                desc: "When a decision needs to become a running system: a senior pod embedded with your team to take an AI use case from discovery to secure production. Also home to our Fractional Technology Leadership engagements.",
              },
              {
                href: "/services/tech-stack-audit",
                label: "Tech Stack Audit",
                desc: "The operational health-check, not the boardroom decision: a productised, fixed-scope review of a live software, cloud, and security stack when you need to know what to fix, modernise, or rebuild.",
              },
              {
                href: "/services/strategy",
                label: "Strategy",
                desc: "Business-aligned technology strategy and roadmaps for organisations planning their next phase of growth.",
              },
              {
                href: "/services/managed-services",
                label: "Managed Services",
                desc: "Ongoing operations, monitoring, and production support for the systems you run once the decision is made.",
              },
              {
                href: "/services/data-center-project-readiness-governance",
                label: "Data Centre Readiness",
                desc: "Data centre readiness and delivery governance: independent risk, vendor, approval, and decision support before and during a captive, DR, or hybrid data centre setup.",
              },
            ].map(link => (
              <Link key={link.href} href={link.href}>
                <a className="block bg-white border border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-sm transition-all group">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{link.desc}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-indigo-500 group-hover:text-indigo-700 transition-colors">
                    Learn more →
                  </span>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ServiceFAQ items={faqItems} />

      <ServiceEngagementLinks />

      <ContactUsHome />

      <Footer />
    </div>
  );
}

export default technologyConsulting;
